import { useEffect, useState } from 'react';
import { api } from '../api';
import type { GuardrailListOut, GuardrailRequestIn, KeyOut } from '../types';

interface GuardrailRequestFormProps {
  keyItem: KeyOut;
  workspaceId: string;
  onSubmit: (data: GuardrailRequestIn) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
  canAssignPermanent?: boolean;
}

/**
 * Form to request a temporary guardrail assignment for a key. Cloned from BudgetRequestForm.
 * Fetches the guardrail catalog from the key's team's workspace and requires a non-empty
 * business justification. Optionally allows permanent assignment for leads/admins.
 */
export function GuardrailRequestForm({
  keyItem,
  workspaceId,
  onSubmit,
  onCancel,
  submitting,
  canAssignPermanent = false,
}: GuardrailRequestFormProps) {
  const [guardrails, setGuardrails] = useState<GuardrailListOut | null>(null);
  const [loadingGuardrails, setLoadingGuardrails] = useState(true);
  const [selectedGuardrailId, setSelectedGuardrailId] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [permanent, setPermanent] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingGuardrails(true);
      try {
        const result = await api.guardrails(workspaceId);
        setGuardrails(result);
        if (result.items.length > 0 && result.items[0]) {
          setSelectedGuardrailId(result.items[0].id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoadingGuardrails(false);
      }
    };
    void load();
  }, [workspaceId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedGuardrailId) {
      setError('Please select a guardrail');
      return;
    }

    if (!reason.trim()) {
      setError('Business justification is required');
      return;
    }

    if (!permanent) {
      const duration = parseInt(durationDays, 10);
      if (isNaN(duration) || duration <= 0) {
        setError('Duration must be a positive number of days');
        return;
      }
    }

    const data: GuardrailRequestIn = {
      key_id: keyItem.id,
      requested_guardrail_id: selectedGuardrailId,
      reason: reason.trim(),
      ...(permanent ? { permanent: true } : { duration_days: parseInt(durationDays, 10) }),
    };

    try {
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (loadingGuardrails) {
    return (
      <div className="create-key-form">
        <h3>Request guardrail assignment</h3>
        <p>Loading guardrails...</p>
      </div>
    );
  }

  if (!guardrails || guardrails.items.length === 0) {
    return (
      <div className="create-key-form">
        <h3>Request guardrail assignment</h3>
        <div className="callout callout-warning">
          <strong>No guardrails available.</strong> No guardrails are defined in this team's workspace.
          Contact your platform admin to create guardrails.
        </div>
        <div className="form-actions">
          <button type="button" onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="create-key-form">
      <h3>Request guardrail assignment</h3>

      <div className="callout callout-warning">
        <strong>Temporary assignment only:</strong> The guardrail will be applied temporarily
        and will automatically revert when the period expires
        {canAssignPermanent ? ', unless marked permanent' : ''}.
      </div>

      <div className="form-group">
        <label htmlFor="key-name">Key</label>
        <input
          id="key-name"
          type="text"
          value={keyItem.name}
          disabled
          style={{ background: 'var(--bg)' }}
        />
      </div>

      <div className="form-group">
        <label htmlFor="guardrail">
          Guardrail <span className="required">*</span>
        </label>
        <select
          id="guardrail"
          value={selectedGuardrailId}
          onChange={(e) => setSelectedGuardrailId(e.target.value)}
          disabled={submitting}
          required
        >
          {guardrails.items.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name ?? g.id}
              {g.description ? ` - ${g.description}` : ''}
            </option>
          ))}
        </select>
      </div>

      {canAssignPermanent && (
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={permanent}
              onChange={(e) => setPermanent(e.target.checked)}
              disabled={submitting}
            />
            <span style={{ marginLeft: '8px' }}>Permanent assignment (no expiry)</span>
          </label>
        </div>
      )}

      {!permanent && (
        <div className="form-group">
          <label htmlFor="duration">Requested duration</label>
          <div className="form-inline">
            <input
              id="duration"
              type="number"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              disabled={submitting}
              min="1"
              style={{ width: '80px' }}
            />
            <span>days</span>
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="reason">
          Business justification (e.g. JIRA ticket) <span className="required">*</span>
        </label>
        <textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="Why do you need this guardrail? Include a JIRA ticket or approval reference."
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          required
        />
        <p className="form-note">This field is required. Help approvers understand the request.</p>
      </div>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'Requesting...' : 'Submit request'}
        </button>
      </div>
    </form>
  );
}
